import { localDayOf } from '@railroute/contracts/core';
import type {
  BriefingJournalResponse,
  BriefingJournalRow,
  BriefingJournalSignature,
} from '@railroute/contracts/core';

// Журнал регистрации инструктажа по безопасности и охране труда — форма
// приложения 17 Правил № 1019 в ред. приказа № 223 (issue #533, порядок граф
// дал Штаб 29.09.2026).
//
// ОДИН СПИСОК ГРАФ НА ВСЁ. Экран, печать (PDF из браузера) и Excel строятся из
// одних и тех же `JOURNAL_COLUMNS`, `journalHeader` и `journalCells`. Три
// раздельных перечня разошлись бы при первой же правке одного из них — а
// порядок граф в журнале установлен приказом, и разойтись ему нельзя.
//
// ЗАПИСЬ — С СЕРВЕРА. Журнал не отдельный ввод: это те же подписи, что в
// наряде (`GET /work-orders/briefing-journal`). Панель ничего не досчитывает:
// год рождения, «профессия, должность» и «Фамилия И. О., должность» приходят
// готовыми, ИИН при этом не расшифровывается нигде.

export const JOURNAL_TITLE = 'Журнал регистрации инструктажа по безопасности и охране труда';

/** Десять граф приложения 17 — ровно в этом порядке и с этими названиями. */
export const JOURNAL_COLUMNS = [
  'Дата',
  'ФИО инструктируемого',
  'Год рождения',
  'Профессия, должность инструктируемого',
  'Вид инструктажа (первичный на рабочем месте, повторный, целевой, внеплановый)',
  'Причина проведения внепланового инструктажа',
  'Фамилия, инициалы, должность инструктирующего',
  'Отметка о прохождении проверки знаний (прошёл / не прошёл)',
  'Подпись инструктирующего',
  'Подпись инструктируемого',
] as const;

/** `YYYY-MM-DD` → `ДД.ММ.ГГГГ`, без часового пояса браузера. */
export function journalDate(day: string): string {
  const [year, month, date] = day.split('-');
  return `${date}.${month}.${year}`;
}

/** Шапка: организация, «Начат» — первый день периода, «Окончен» — последний. */
export function journalHeader(journal: Pick<BriefingJournalResponse, 'organizationName' | 'from' | 'to'>): [string, string][] {
  return [
    ['Организация', journal.organizationName],
    ['Начат', journalDate(journal.from)],
    ['Окончен', journalDate(journal.to)],
  ];
}

/**
 * Как подписано — словами. Ни одна графа не выдаёт подпись без ЭЦП за ЭЦП:
 * ключ устройства назван прямо «без ЭЦП».
 */
export function signatureText(signature: BriefingJournalSignature | null): string {
  if (!signature) return '';
  switch (signature.method) {
    case 'QUALIFIED_ECP':
      return 'ЭЦП';
    case 'DEVICE_KEY':
      return 'ключ устройства, без ЭЦП';
    case 'PAPER_JOURNAL':
      return 'на бумаге, фото';
    case 'UNSIGNED_MARK':
      return 'отметка без подписи';
    case 'PROXY_WITHDRAWN':
      return 'отметка за работника — способ отменён';
  }
}

/** Графа 8: «прошёл» / «не прошёл»; результата не записано — пусто. */
export function knowledgeText(passed: boolean | null): string {
  if (passed === true) return 'прошёл';
  if (passed === false) return 'не прошёл';
  return '';
}

/**
 * Графа 5: вид инструктажа. Целевой — по наряду (#531); первичный на рабочем
 * месте, повторный и внеплановый — записью журнала без наряда (#543).
 */
const KIND_TEXT: Record<BriefingJournalRow['briefingKind'], string> = {
  TARGET: 'целевой',
  WORKPLACE_INITIAL: 'первичный на рабочем месте',
  REPEATED: 'повторный',
  UNSCHEDULED: 'внеплановый',
};

/** Пометка строки целевого инструктажа по наряду, выданному без ЭЦП. */
export const ISSUE_UNSIGNED_NOTE = 'наряд выдан без ЭЦП';

/** Графа 5 словами — тем же перечнем, что в выгрузке. */
export function kindText(kind: BriefingJournalRow['briefingKind']): string {
  return KIND_TEXT[kind];
}

/**
 * Значения десяти граф одной строки — в порядке `JOURNAL_COLUMNS`. Пустое —
 * пустым, а не выдуманным.
 */
export function journalCells(row: BriefingJournalRow): string[] {
  return [
    journalDate(row.date),
    row.briefedName,
    row.birthYear === null ? '' : String(row.birthYear),
    row.professionAndPosition ?? '',
    // Стенд 30.09.2026: наряд выдан без ЭЦП выдающего, подписи инструктажа
    // опираются на неподписанную выдачу — пометка в той же графе, и на
    // экране, и в печати, и в Excel.
    row.issueUnsigned ? `${KIND_TEXT[row.briefingKind]} (${ISSUE_UNSIGNED_NOTE})` : KIND_TEXT[row.briefingKind],
    row.unscheduledReason ?? '',
    row.conductorLabel ?? '',
    knowledgeText(row.knowledgePassed),
    signatureText(row.conductorSignature),
    signatureText(row.workerSignature),
  ];
}

/** Лист целиком: заголовок, шапка, пустая строка, названия граф, строки. */
export function journalSheet(journal: BriefingJournalResponse): string[][] {
  return [
    [JOURNAL_TITLE],
    ...journalHeader(journal).map(([label, value]) => [label, value]),
    [],
    [...JOURNAL_COLUMNS],
    ...journal.rows.map(journalCells),
  ];
}

/**
 * Первый день месяца и сегодня — период по умолчанию, `YYYY-MM-DD`.
 * «Сегодня» — по времени организации, тем же правилом, что дата журнала на
 * сервере, а не по часам компьютера, на котором открыта панель.
 */
export function defaultPeriod(today: Date): { from: string; to: string } {
  const day = localDayOf(today);
  return { from: `${day.slice(0, 8)}01`, to: day };
}
